import { logger_v2 as logger, LogType } from '@absis/shared';
import {
  checkEviction,
  EvectionCtx,
  getEvictionTS,
  IParamsStore,
  TicketOptions,
  TicketValue,
} from './commmon';

/**
 * Shared state.
 */
declare global {
  interface Window {
    __GlobalOpenFrontParamsDB__: ParamsDB;
  }
}

const EVICTION_CHECK = 30 * 1000,
  MAX_PROCESSED_TICKETS = 20;

interface ParamsDB {
  evictionTimeout: ReturnType<typeof setTimeout> | undefined;
  connection: Promise<IDBDatabase> | undefined;
}

function rPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

function getDBID(ticket: string): number | undefined {
  let result;
  const match = /^tck([0-9]+)$/.exec(ticket);
  if (match && match[1]) {
    result = Number(match[1]);
  }
  return result;
}

export class IndexedDBStore implements IParamsStore {
  static DB_VERSION = 1;
  static DB_NAME = 'opfParamInterchange';
  private paramsDB: ParamsDB;
  constructor() {
    const currentWindow = window.top ? window.top : window;
    if (!currentWindow.__GlobalOpenFrontParamsDB__) {
      currentWindow.__GlobalOpenFrontParamsDB__ = {
        evictionTimeout: undefined,
        connection: undefined,
      };
    }
    this.paramsDB = currentWindow.__GlobalOpenFrontParamsDB__;
  }

  public async ticket(data: unknown, options?: TicketOptions): Promise<string> {
    const eviction = options && options.eviction ? options.eviction : 'onfetch';
    if (eviction === 'never') {
      throw new Error('"never" eviction not supported');
    }
    const ticketValue: TicketValue = {
      value: data,
      evictionTS: getEvictionTS(eviction),
      eviction,
    };
    const id = await this.createTicket(ticketValue);
    this.checkEvictionTimeout();
    return `tck${id}`;
  }

  public async ticketValue<T>(ticket: string): Promise<T | undefined> {
    const dbid = getDBID(ticket);
    if (dbid) {
      const db = await this.getDB();
      const trans = db.transaction('parameters', 'readwrite'),
        transPromise = tPromise(trans);
      const store = trans.objectStore('parameters');
      const getPromise = rPromise(store.get(dbid));

      const ticketValue: TicketValue = await getPromise;
      if (ticketValue) {
        const context = { currentTS: Date.now(), fetch: true };
        if (checkEviction(ticketValue, context)) {
          await Promise.all([rPromise(store.delete(dbid)), transPromise]);
        }
        context.fetch = false;
        if (!checkEviction(ticketValue, context)) {
          return ticketValue.value as T;
        }
      }
    }
    return;
  }

  public async removeTicket(ticket: string): Promise<void> {
    const dbid = getDBID(ticket);
    if (dbid) {
      const db = await this.getDB();
      const trans = db.transaction('parameters', 'readwrite'),
        transPromise = tPromise(trans);
      await Promise.all([
        rPromise(trans.objectStore('parameters').delete(dbid)),
        transPromise,
      ]);
    }
  }

  private async createTicket(ticketValue: TicketValue): Promise<string> {
    const db = await this.getDB();
    const trans = db.transaction('parameters', 'readwrite');
    const transPromise = tPromise(trans);
    const addPromise = rPromise(
      trans.objectStore('parameters').add(ticketValue)
    );
    return Promise.all([addPromise, transPromise]).then(result =>
      result[0].toString()
    );
  }

  private getDB(): Promise<IDBDatabase> {
    if (!this.paramsDB.connection) {
      this.paramsDB.connection = new Promise<IDBDatabase>((resolve, reject) => {
        const openRequest = window.indexedDB.open(
          IndexedDBStore.DB_NAME,
          IndexedDBStore.DB_VERSION
        );
        openRequest.onsuccess = () => resolve(openRequest.result);
        openRequest.onerror = () => reject(openRequest.error);
        openRequest.onblocked = () =>
          reject(new Error("Can't update database"));
        openRequest.onupgradeneeded = () => {
          const db = openRequest.result;
          const paramStore = db.createObjectStore('parameters', {
            keyPath: 'ticket',
            autoIncrement: true,
          });
          paramStore.createIndex('evictionTS', 'evictionTS');
        };
      });
    }
    return this.paramsDB.connection;
  }

  private checkEvictionTimeout(): void {
    if (this.paramsDB.evictionTimeout === undefined) {
      this.paramsDB.evictionTimeout = setTimeout(
        this.removeEvictedTickets.bind(this),
        EVICTION_CHECK
      );
    }
  }

  private async paramsCount(trans: IDBTransaction): Promise<number> {
    let result;
    try {
      result = await rPromise(trans.objectStore('parameters').count());
    } catch (error) {
      result = -1;
      logger({
        loglevel: LogType.WARNING,
        message:
          '[BRIDGE-SERVICE] [APP-BRIDGE] error in ticket count: ' +
          (error as Error).message,
      });
    }
    return result;
  }

  private async removeEvictedTickets(): Promise<void> {
    try {
      const db = await this.getDB();
      const trans = db.transaction('parameters', 'readwrite');
      const context: EvectionCtx = {
        currentTS: Date.now(),
        fetch: false,
      };
      const cursorReq = trans
        .objectStore('parameters')
        .index('evictionTS')
        .openCursor();
      let processed = 0;
      await new Promise<void>((resolve, reject) => {
        cursorReq.onsuccess = event => {
          try {
            const target =
              event.target as IDBRequest<IDBCursorWithValue> | null;
            const cursor = target ? target.result : null;
            if (cursor) {
              const ticketValue: TicketValue = cursor.value;
              if (checkEviction(ticketValue, context)) {
                cursor.delete();
                processed += 1;
                if (processed < MAX_PROCESSED_TICKETS) {
                  cursor.continue();
                } else {
                  resolve();
                }
              } else {
                resolve();
              }
            } else {
              resolve();
            }
          } catch (error) {
            reject(error);
          }
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      }).then(async () => {
        const paramsCount = await this.paramsCount(trans);
        this.paramsDB.evictionTimeout = undefined;
        // This is a lazy process and remove all tickets is not
        // mandatory. paramsCount may be zero but a new record is
        // queued, in this case we stop the timer but some
        // tickets are present (those tickets will be deleted in the next ticket creation).
        if (paramsCount) {
          this.checkEvictionTimeout();
        }
      });
    } catch (error) {
      this.paramsDB.evictionTimeout = undefined;
      this.checkEvictionTimeout();
      logger({
        loglevel: LogType.WARNING,
        message:
          '[BRIDGE-SERVICE] [APP-BRIDGE] error removing tickets: ' +
          (error as Error).message,
      });
    }
  }
}
